import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { createPage, deletePage, updatePage } from '../../../services/pages';

// Транслитерация кириллицы в латиницу для URL-friendly slug
const transliterate = (text) => {
  const map = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
  };
  return text.toLowerCase().split('').map(char => map[char] || char).join('');
};

const PageSelector = ({
  pages,
  currentPage,
  onPageChange,
  onPagesUpdate,
  projectId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [newPageName, setNewPageName] = useState('');
  const [editName, setEditName] = useState('');

  // Генерация slug из названия
  const generateSlug = (name) => {
    return transliterate(name)
      .replace(/[^a-z0-9\s-]/gi, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handlePageSelect = async (page) => {
    if (page.id === currentPage?.id) {
      setIsOpen(false);
      return;
    }

    onPageChange(page);
    setIsOpen(false);
  };

  const handleCreatePage = async () => {
    if (!newPageName.trim()) return;

    const slug = generateSlug(newPageName);
    const maxOrder = Math.max(...pages.map(p => p.order || 0), 0);

    const newPage = await createPage(projectId, {
      name: newPageName.trim(),
      slug: slug || `page-${Date.now()}`,
      sections: [],
      order: maxOrder + 1
    });

    // Формируем полный объект страницы для локального состояния
    const newPageWithDefaults = {
      ...newPage,
      sections: newPage.sections || [],
    };

    const updatedPages = [...pages, newPageWithDefaults];

    // Обновляем список и сразу переключаемся на новую страницу
    onPagesUpdate(updatedPages, newPageWithDefaults);

    setNewPageName('');
    setIsCreating(false);
    setIsOpen(false);
  };

  const handleDeletePage = async (e, page) => {
    e.stopPropagation();

    if (pages.length <= 1) {
      alert('Нельзя удалить единственную страницу');
      return;
    }

    if (!window.confirm(`Удалить страницу "${page.name}"?`)) {
      return;
    }

    await deletePage(projectId, page.id);

    const updatedPages = pages.filter(p => p.id !== page.id);
    onPagesUpdate(updatedPages);

    if (currentPage?.id === page.id) {
      onPageChange(updatedPages[0]);
    }
  };

  const handleStartEdit = (e, page) => {
    e.stopPropagation();
    setIsEditing(page.id);
    setEditName(page.name);
  };

  const handleSaveEdit = async (e, page) => {
    e.stopPropagation();

    if (!editName.trim()) return;

    const newSlug = generateSlug(editName);
    await updatePage(projectId, page.id, {
      name: editName.trim(),
      slug: newSlug || page.slug
    });

    const updatedPages = pages.map(p =>
      p.id === page.id ? { ...p, name: editName.trim(), slug: newSlug || p.slug } : p
    );
    onPagesUpdate(updatedPages);
    setIsEditing(null);
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(null);
  };

  const isHomePage = (page) => {
    return page.slug === '' || page.slug === 'index' || page.order === 0;
  };

  return (
    <div className="relative">
      {/* Current page button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors min-w-[140px]"
      >
        <Icon name="FileText" size={16} className="text-text-secondary" />
        <span className="font-medium truncate max-w-[120px]">
          {currentPage?.name || 'Страница'}
        </span>
        <Icon
          name="ChevronDown"
          size={16}
          className={`text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
              setIsCreating(false);
              setIsEditing(null);
            }}
          />

          {/* Menu */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-xl shadow-elevation-3 z-50 overflow-hidden">
            <div className="p-2 border-b border-border">
              <div className="text-xs font-medium text-text-secondary uppercase tracking-wide px-2 py-1">
                Страницы ({pages.length})
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto p-2">
              {pages.map((page) => (
                <div
                  key={page.id}
                  onClick={() => !isEditing && handlePageSelect(page)}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                    currentPage?.id === page.id
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted'
                  }`}
                >
                  {isEditing === page.id ? (
                    <div className="flex-1 space-y-2" onClick={e => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-border rounded bg-background"
                        placeholder="Название"
                        autoFocus
                      />
                      {!isHomePage(page) && (
                        <div className="text-xs text-text-secondary px-1">
                          /{generateSlug(editName) || 'slug'}
                        </div>
                      )}
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => handleSaveEdit(e, page)}
                          className="flex-1 px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:opacity-90"
                        >
                          Сохранить
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-2 py-1 text-xs border border-border rounded hover:bg-muted"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Icon
                        name={isHomePage(page) ? 'Home' : 'FileText'}
                        size={16}
                        className={currentPage?.id === page.id ? 'text-primary' : 'text-text-secondary'}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{page.name}</div>
                        <div className="text-xs text-text-secondary truncate">
                          /{isHomePage(page) ? '' : page.slug}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => handleStartEdit(e, page)}
                          className="p-1 hover:bg-background rounded"
                          title="Редактировать"
                        >
                          <Icon name="Pencil" size={14} />
                        </button>
                        {!isHomePage(page) && (
                          <button
                            onClick={(e) => handleDeletePage(e, page)}
                            className="p-1 hover:bg-destructive/10 hover:text-destructive rounded"
                            title="Удалить"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Create new page */}
            <div className="p-2 border-t border-border">
              {isCreating ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newPageName}
                    onChange={(e) => setNewPageName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreatePage()}
                    className="w-full px-3 py-2 text-base md:text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Название страницы"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreatePage}
                      disabled={!newPageName.trim()}
                      className="flex-1 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
                    >
                      Создать
                    </button>
                    <button
                      onClick={() => {
                        setIsCreating(false);
                        setNewPageName('');
                      }}
                      className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted"
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsCreating(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                >
                  <Icon name="Plus" size={16} />
                  Добавить страницу
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PageSelector;
